import ThemedSelect from "#/components/shared/themed-select";
import { extractPatientNameFromContract } from "#/lib/contractFormValues";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { statusOptions } from "../../api/medical-record-requests.type";
import Card from "../../components/shared/card";
import Typography from "../../components/shared/typography";
import { useMedicalRecordRequestDetailContext } from "../context/MedicalRecordRequestDetailContext";

export function RequestSummaryCard() {
  const {
    request,
    details,
    isEditable,
    isUpdatingStatus,
    isSendingNow,
    isMarkingReady,
    isRegenerating,
    isUploadingDocument,
    isLookupLoading,
    onSendNow,
    onMarkReady,
    onLookupFax,
    onEditContactInfo,
    onRegeneratePdf,
    onUploadDocument,
    onUpdateStatus,
    onActionSelect,
  } = useMedicalRecordRequestDetailContext();

  // All hooks must be called before any conditional returns
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [statusValue, setStatusValue] = useState(request?.status ?? "");
  const actionOptions = [
    onSendNow ? { value: "send", label: "Send now" } : null,
    onMarkReady ? { value: "markReady", label: "Mark ready to send" } : null,
    onLookupFax ? { value: "lookupFax", label: "Look up fax number" } : null,
    typeof onEditContactInfo === "function"
      ? { value: "editContact", label: "Edit contact info" }
      : null,
    typeof onRegeneratePdf === "function"
      ? { value: "regeneratePdf", label: "Regenerate PDF" }
      : null,
    typeof onUploadDocument === "function"
      ? { value: "replaceDocument", label: "Replace document" }
      : null,
  ].filter(Boolean) as { value: string; label: string }[];
  const [actionValue, setActionValue] = useState<string>(
    actionOptions[0]?.value ?? ""
  );

  // Sync statusValue with request.status when request changes
  useEffect(() => {
    if (request?.status) {
      setStatusValue(request.status);
    }
  }, [request?.status]);

  if (!request) {
    return null;
  }

  const patientName =
    request.patientName ??
    request.patient_name ??
    extractPatientNameFromContract(request as unknown as Record<string, any>);
  const recipientName = request.recipientName ?? request.recipient_name ?? null;
  const patientLabel = patientName || request.patientId || "Unknown patient";
  const recipientLabel = recipientName || "Unknown institution";
  const headerTitle = `${patientLabel} → ${recipientLabel}`;
  const createdAt = new Date(request.createdAt).toLocaleString();

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onUploadDocument) {
      onUploadDocument(file);
      event.target.value = "";
    }
  };

  const handleStatusChange = async (next: string) => {
    setStatusValue(next);
    if (isUpdatingStatus || !onUpdateStatus) return;
    try {
      await onUpdateStatus(next);
    } catch (err) {
      console.error("Unable to update status", err);
    }
  };

  const handleActionChange = async (next: string) => {
    setActionValue(next);
    if (next === "replaceDocument" && onUploadDocument) {
      fileInputRef.current?.click();
      return;
    }
    if (!onActionSelect) return;
    try {
      await onActionSelect(next);
    } catch (err) {
      console.error("Action failed", err);
    }
  };

  return (
    <Card variant="cream" elevation={1}>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div>
            <Typography variant="h3" className="text-terracotta">
              {headerTitle}
            </Typography>
            <Typography variant="body2" className="text-terracotta/70">
              Request {request.id}
            </Typography>
            <Typography variant="body2" className="text-terracotta/60">
              Created {createdAt}
            </Typography>
          </div>
          {isEditable && (
            <div className="flex flex-col gap-3">
              {onUpdateStatus && (
                <div className="space-y-2 rounded-lg border border-cream-300 bg-cream-50 p-3">
                  <div className="min-w-[260px]">
                    <ThemedSelect
                      label="Status"
                      value={statusValue}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      options={statusOptions.map((status) => ({
                        value: status,
                        label: status.replace(/_/g, " "),
                      }))}
                    />
                  </div>
                  <div className="text-sm text-brown-600">
                    {isUpdatingStatus
                      ? "Updating..."
                      : "Select status to apply"}
                  </div>
                </div>
              )}
              {actionOptions.length > 0 && (
                <div className="space-y-2 rounded-lg border border-cream-300 bg-cream-50 p-3">
                  <div className="min-w-[260px]">
                    <ThemedSelect
                      label="Actions"
                      value={actionValue}
                      onChange={(e) => handleActionChange(e.target.value)}
                      options={actionOptions.map((opt) => ({
                        value: opt.value,
                        label: opt.label,
                      }))}
                    />
                  </div>
                  <div className="text-sm text-brown-600">
                    {isSendingNow ||
                    isMarkingReady ||
                    isRegenerating ||
                    isUploadingDocument ||
                    isLookupLoading
                      ? "Working..."
                      : "Select an action to run it"}
                  </div>
                  {onUploadDocument && (
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        <dl className="grid gap-4 md:grid-cols-2">
          {details.map((detail) => (
            <div key={detail.label}>
              <dt className="text-sm text-terracotta/70 uppercase tracking-wide">
                {detail.label}
              </dt>
              <dd className="text-base text-terracotta">
                {detail.href && detail.value ? (
                  <a
                    href={detail.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-terracotta-600 hover:text-terracotta-700"
                  >
                    {detail.value}
                  </a>
                ) : (
                  detail.value || "—"
                )}
              </dd>
            </div>
          ))}
        </dl>
      </Card.Body>
    </Card>
  );
}

export default RequestSummaryCard;
